import { asyncForEach, asyncMap, asyncMapSequential, safeInteropRegister, toGoLangInvoke } from "../../api-polyfill/src/common/windowFunctions";
import { stagePriorityMap } from "../../api-polyfill/src/common/configToGlue";
export class BootService {
    constructor(glue, logger, router) {
        this.glue = glue;
        this.logger = logger;
        this.router = router;
        this.startupTimes = {
            stageTimes: [],
            cumulativeTimes: 0
        };
        this.currentStage = "";
        this.currentStageStartTime = 0;
        this.stageWhenToChannelsMap = {};
        this.executedStages = {};
    }
    async waitForBootStageHandler(params) {
        const stage = stagePriorityMap[params.stage][0] ?? params.stage;
        const key = `${stage}:${params.when}`;
        this.logger.debug("boot-sequence - bootService.waitForBootStageHandler", params, "converted to", key);
        if (this.executedStages[key]) {
            const { err } = await toGoLangInvoke(() => this.glue.interop.invoke(params.channel, {}));
            if (err) {
                this.logger.error("boot-sequence - Error calling waitForBootStage remote channel", params, err);
            }
        }
        else {
            this.stageWhenToChannelsMap[key] = (this.stageWhenToChannelsMap[key] ?? []).concat(params.channel);
        }
    }
    async signal(stage, when) {
        this.logger.debug("boot-sequence - bootService:systemManager.boot.stage", stage, when);
        if (when === "stageEntered") {
            this.currentStage = stage;
            this.currentStageStartTime = performance.now();
        }
        else {
            this.router.publish("systemManager.boot.stage", { stage });
            const bootStageStartupTime = Math.round((performance.now() - this.currentStageStartTime + Number.EPSILON) * 100) / 100;
            this.startupTimes.stageTimes.push({ [this.currentStage]: bootStageStartupTime });
            this.startupTimes.cumulativeTimes = this.startupTimes.cumulativeTimes + bootStageStartupTime;
        }
        const key = `${stage}:${when}`;
        await asyncMap(this.stageWhenToChannelsMap[key] ?? [], async (channel) => {
            this.logger.debug("boot-sequence - bootService.signal", key, channel);
            await toGoLangInvoke(() => this.glue.interop.invoke(channel, {})).catch((err) => {
                this.logger.error("boot-sequence - Error signaling waitForBooStage", key, channel, err);
            });
        });
        delete this.stageWhenToChannelsMap[key];
        this.executedStages[key] = true;
    }
    async updateBootStage({ stage, when }) {
        this.logger.debug("boot-sequence - bootService.updateBootStage", stage, when);
        const stagesWithoutPhase = Object.keys(stagePriorityMap);
        const combos = stagesWithoutPhase.flatMap((stage) => [`${stage}:stageEntered`, `${stage}:stageCompleted`]);
        const key = `${stage}:${when}`;
        let combosToSignal = [];
        for (let i = 0; i < combos.length; i++) {
            const combo = combos[i];
            if (!this.executedStages[combo]) {
                combosToSignal.push(combo);
            }
            if (combo === key) {
                if (key === "earlyuser:stageCompleted") {
                    combosToSignal.push("user:stageEntered");
                    setTimeout(() => {
                        this.updateBootStage({ stage: "user", when: "stageCompleted" });
                    }, 5000);
                }
                break;
            }
        }
        await asyncMapSequential(combosToSignal, async (combo) => {
            const parts = combo.split(":");
            await this.signal(parts[0], parts[1]);
        });
    }
    async onShuttingDown(args) {
        return new Promise(async (resolve) => {
            if (args.restarting) {
                await this.router.transmit("Application.restart", {});
            }
            else {
                await this.router.transmit("Application.shutdown", {});
            }
            setTimeout(() => {
                resolve({ prevent: false });
            }, 3000);
        });
    }
    async receiveAutoStart(info) {
        asyncForEach(Object.entries(stagePriorityMap), async ([key, value]) => {
            if (value[0] === info.stage && value[1] === info.runPriority) {
                await this.updateBootStage({ stage: key, when: info.phase === "stageStarting" ? "stageEntered" : "stageCompleted" });
            }
        });
    }
    async start() {
        await this.glue.interop.register("T42.AppManager.AutoStart.Stage", (info) => (this.receiveAutoStart(info)));
        await this.glue.interop.register("FSBL.SystemManagerClient.waitForBootStage", safeInteropRegister(async (params, caller) => this.waitForBootStageHandler(params), this.logger));
        await this.glue.appManager.onShuttingDown((args) => this.onShuttingDown(args));
        await this.router.addResponder("finsemble.startuptimes", (err, message) => {
            message?.sendQueryResponse(null, this.startupTimes);
        });
    }
}
